import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { differenceInDays, format } from "date-fns";
import { useState, useTransition } from "react";
import {
	graphql,
	useFragment,
	useLazyLoadQuery,
	useMutation,
	usePaginationFragment,
	useRefetchableFragment,
} from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { Icon } from "@/components/icon";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { OfferAssociateDialog } from "@/dialogs/offer-associate-dialog";
import {
	getHistoryEventIcon,
	HistoryEventDescription,
} from "@/ui/history-event";
import { MemberBadge } from "@/ui/member-badge";
import type { ClubMemberQuery } from "./__generated__/ClubMemberQuery.graphql";
import type { membros_clubMember$key } from "./__generated__/membros_clubMember.graphql";
import type { membros_usernameIndexHistoryFragment$key } from "./__generated__/membros_usernameIndexHistoryFragment.graphql";
import type { membros_usernameIndexHistoryPaginationQuery } from "./__generated__/membros_usernameIndexHistoryPaginationQuery.graphql";
import type { membros_usernameIndexMemberFragment$key } from "./__generated__/membros_usernameIndexMemberFragment.graphql";
import type { membros_usernameIndexQuery } from "./__generated__/membros_usernameIndexQuery.graphql";

const defaultValues = {
	view: "acessos",
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/audiencia/membros/$username/",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			view: z.enum(["acessos", "atividades"]).default("acessos"),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { username } = Route.useParams();
	const [_, startTransition] = useTransition();

	const clubData = useLazyLoadQuery<membros_usernameIndexQuery>(
		graphql`
			query membros_usernameIndexQuery($clubSlug: String!, $username: String!) {
				club(slug: $clubSlug) {
					...membros_clubMember @arguments(username: $username)
				}
			}
		`,
		{ clubSlug: club?.slug ?? "", username },
	);

	const [data, refetchClubMember] = useRefetchableFragment<
		ClubMemberQuery,
		membros_clubMember$key
	>(
		graphql`
    fragment membros_clubMember on Club @refetchable(queryName: "ClubMemberQuery")
    @argumentDefinitions(username: { type: "String!" })
    {
      clubMember(username: $username) {
        ...membros_usernameIndexMemberFragment
      }
    }
  `,
		clubData.club,
	);

	const member = data.clubMember;

	if (!member) {
		return (
			<div className="p-6">
				<div className="border rounded-md p-6 text-center">
					<Icon
						name="UserX"
						className="size-12 text-muted-foreground mx-auto mb-4"
					/>
					<h3 className="text-lg font-semibold mb-2">Membro não encontrado</h3>
					<p className="text-muted-foreground">
						O membro com username "{username}" não foi encontrado.
					</p>
				</div>
			</div>
		);
	}

	return (
		<MemberDetail
			member={member}
			onRefresh={() =>
				startTransition(() => {
					refetchClubMember({ username }, { fetchPolicy: "network-only" });
				})
			}
		/>
	);
}

function MemberDetail({
	member: memberRef,
	onRefresh,
}: {
	member: membros_usernameIndexMemberFragment$key;
	onRefresh: () => void;
}) {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const navigate = useNavigate({ from: Route.fullPath });
	const { view } = useSearch({ from: Route.id });
	const [isOfferDialogOpen, setIsOfferDialogOpen] = useState(false);

	const member = useFragment(
		graphql`
			fragment membros_usernameIndexMemberFragment on ClubMember {
				id
				joinedAt
				points
				isAdmin
				isModerator
				user {
					displayName
					username
					email
					bio
					photo {
						signedUrl
					}
					avatarFallback
				}
				accessGroups(first: 50) {
					edges {
						node {
							id
							createdAt
							expiresAt
							pausedAt
							accessGroup {
								id
								externalProducts {
									id
									paymentIntegrationProduct {
										id
										productName
									}
								}
							}
						}
					}
				}
				courses(first: 50) {
					edges {
						node {
							id
							enrolledAt
							expiresAt
							memberGroup {
								group {
									name
								}
							}
							course {
								name
								slug
								category {
									name
								}
							}
						}
					}
				}
				spaces(first: 50) {
					edges {
						node {
							joinedAt
							expiresAt
							space {
								id
								name
								slug
								visibility
								group {
									name
								}
							}
						}
					}
				}
				tags {
					id
					text
				}
				badges {
					id
					...memberBadgeFragment
				}
				...membros_usernameIndexHistoryFragment
			}
		`,
		memberRef,
	);

	const {
		data: historyData,
		loadNext,
		hasNext,
		isLoadingNext,
	} = usePaginationFragment<
		membros_usernameIndexHistoryPaginationQuery,
		membros_usernameIndexHistoryFragment$key
	>(
		graphql`
			fragment membros_usernameIndexHistoryFragment on ClubMember
			@refetchable(queryName: "membros_usernameIndexHistoryPaginationQuery")
			@argumentDefinitions(
				first: { type: "Int", defaultValue: 10 }
				after: { type: "String" }
			) {
				history(first: $first, after: $after)
					@connection(key: "membros_usernameIndexHistoryFragment_history") {
					edges {
						node {
							__typename
							id
							createdAt
							... on BadgeAssignedUserHistory {
								badge {
									text
									...memberBadgeFragment
								}
							}
							... on BadgeUnassignedUserHistory {
								badge {
									text
									...memberBadgeFragment
								}
							}
							... on CertificateEmittedUserHistory {
								certificate {
									course {
										name
									}
								}
							}
							... on CompletedClassUserHistory {
								courseClass {
									name
									section {
										course {
											name
										}
									}
								}
							}
							... on CompletedCourseUserHistory {
								course {
									name
								}
							}
							... on CourseCommentCreatedUserHistory {
								comment {
									... on CourseComment {
										courseClass {
											name
											section {
												course {
													name
												}
											}
										}
									}
								}
							}
							... on EventAttendanceUserHistory {
								event {
									title
								}
							}
							... on EventCommentCreatedUserHistory {
								comment {
									... on EventComment {
										event {
											title
										}
									}
								}
							}
							... on PostCreatedUserHistory {
								post {
									... on Post {
										title
										space {
											name
										}
									}
								}
							}
							... on PostCommentCreatedUserHistory {
								comment {
									... on Comment {
										post {
											title
										}
									}
								}
							}
							... on SubscriptionStartedUserHistory {
								accessGroupUser {
									accessGroup {
										externalProducts {
											paymentIntegrationProduct {
												productName
											}
										}
									}
								}
							}
							... on SubscriptionEndedUserHistory {
								accessGroupUser {
									accessGroup {
										externalProducts {
											paymentIntegrationProduct {
												productName
											}
										}
									}
								}
							}
							... on SubscriptionPausedUserHistory {
								accessGroupUser {
									accessGroup {
										externalProducts {
											paymentIntegrationProduct {
												productName
											}
										}
									}
								}
							}
							... on SubscriptionResumedUserHistory {
								accessGroupUser {
									accessGroup {
										externalProducts {
											paymentIntegrationProduct {
												productName
											}
										}
									}
								}
							}
							... on TagAssignedUserHistory {
								tag {
									text
								}
							}
							... on TagUnassignedUserHistory {
								tag {
									text
								}
							}
							... on InviteAcceptedUserHistory {
								invite {
									type
								}
							}
						}
					}
				}
			}
		`,
		member,
	);

	const [pauseAccessGroupUser] = useMutation(graphql`
		mutation membros_usernameIndexPauseMutation($accessGroupUser: ID!) {
			accessGroupUserPause(accessGroupUser: $accessGroupUser) {
				id
				pausedAt
			}
		}
	`);

	const [resumeAccessGroupUser] = useMutation(graphql`
		mutation membros_usernameIndexResumeMutation(
			$accessGroupUser: ID!
			$extendExpiration: Boolean!
		) {
			accessGroupUserResume(
				accessGroupUser: $accessGroupUser
				extendExpiration: $extendExpiration
			) {
				id
				pausedAt
				expiresAt
			}
		}
	`);

	const [updateExpiration] = useMutation(graphql`
		mutation membros_usernameIndexUpdateExpirationMutation(
			$accessGroup: ID!
			$expiration: DateTime!
		) {
			accessGroupUserUpdateExpiration(
				accessGroup: $accessGroup
				expiration: $expiration
			) {
				id
				expiresAt
			}
		}
	`);

	const [pauseDialogId, setPauseDialogId] = useState<string | null>(null);
	const [resumeDialogId, setResumeDialogId] = useState<string | null>(null);
	const [extendExpiration, setExtendExpiration] = useState(false);
	const [editDeadlineDialogId, setEditDeadlineDialogId] = useState<
		string | null
	>(null);
	const [editDeadlineValue, setEditDeadlineValue] = useState("");
	const [cancelDialogId, setCancelDialogId] = useState<string | null>(null);

	const [offersTab, setOffersTab] = useState<"ativas" | "canceladas">("ativas");

	const user = member.user;
	const accessGroups = member.accessGroups.edges
		.map((edge) => {
			const node = edge.node;
			const isExpired = node.expiresAt && new Date(node.expiresAt) < new Date();
			return {
				...node,
				isActive: !isExpired,
			};
		})
		.filter((entry) =>
			entry.accessGroup.externalProducts.some(
				(p) => p.paymentIntegrationProduct?.productName,
			),
		);

	const activeOffers = accessGroups.filter((g) => g.isActive);
	const cancelledOffers = accessGroups.filter((g) => !g.isActive);
	const filteredOffers =
		offersTab === "ativas" ? activeOffers : cancelledOffers;

	const courses = member.courses.edges.map((edge) => {
		const node = edge.node;
		const isExpired = node.expiresAt && new Date(node.expiresAt) < new Date();
		return {
			...node,
			isActive: !isExpired,
		};
	});

	const spaces = member.spaces.edges.map((edge) => {
		const node = edge.node;
		const isExpired = node.expiresAt && new Date(node.expiresAt) < new Date();
		return {
			...node,
			isActive: !isExpired,
		};
	});

	const onViewChange = (newView: string) => {
		navigate({
			search: (s) => ({ ...s, view: newView as "acessos" | "atividades" }),
		});
	};

	const resumeEntry = accessGroups.find((g) => g.id === resumeDialogId);
	const daysPaused = resumeEntry?.pausedAt
		? differenceInDays(new Date(), new Date(resumeEntry.pausedAt))
		: 0;

	const handlePause = (accessGroupUserId: string) => {
		pauseAccessGroupUser({
			variables: { accessGroupUser: accessGroupUserId },
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Assinatura pausada com sucesso!");
				setPauseDialogId(null);
				onRefresh();
			},
			onError: () => {
				toast.error("Erro ao pausar assinatura. Tente novamente.");
			},
		});
	};

	const handleResume = (accessGroupUserId: string) => {
		resumeAccessGroupUser({
			variables: {
				accessGroupUser: accessGroupUserId,
				extendExpiration,
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Assinatura retomada com sucesso!");
				setResumeDialogId(null);
				setExtendExpiration(false);
				onRefresh();
			},
			onError: () => {
				toast.error("Erro ao retomar assinatura. Tente novamente.");
			},
		});
	};

	const handleUpdateExpiration = (
		accessGroupUserId: string,
		expiresAt: string,
	) => {
		updateExpiration({
			variables: {
				accessGroup: accessGroupUserId,
				expiration: new Date(expiresAt).toISOString(),
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Prazo atualizado com sucesso!");
				setEditDeadlineDialogId(null);
				setEditDeadlineValue("");
				onRefresh();
			},
			onError: () => {
				toast.error("Erro ao atualizar prazo. Tente novamente.");
			},
		});
	};

	const handleCancelOffer = (accessGroupUserId: string) => {
		updateExpiration({
			variables: {
				accessGroup: accessGroupUserId,
				expiration: new Date().toISOString(),
			},
			onCompleted: (_response, errors) => {
				if (errors?.length) {
					toast.error(errors[0].message);
					return;
				}
				toast.success("Oferta cancelada com sucesso!");
				setCancelDialogId(null);
				onRefresh();
			},
			onError: () => {
				toast.error("Erro ao cancelar oferta. Tente novamente.");
			},
		});
	};

	return (
		<div className="p-6">
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Left Column - Member Summary */}
				<div className="lg:col-span-1">
					<div className="rounded-md border">
						<div className="p-6 pb-4">
							<div className="flex flex-col items-center text-center">
								<Avatar className="size-24 mb-4">
									<AvatarImage
										src={user.photo?.signedUrl ?? undefined}
										alt={user.displayName ?? user.username ?? ""}
									/>
									<AvatarFallback className="text-2xl">
										{user.avatarFallback}
									</AvatarFallback>
								</Avatar>

								<h2 className="text-xl font-semibold">
									{user.displayName ?? user.username}
								</h2>

								{user.username && (
									<p className="text-sm text-muted-foreground">
										@{user.username}
									</p>
								)}

								<div className="flex items-center gap-2 mt-2">
									{member.isAdmin && (
										<Badge variant="outline" className="text-xs">
											ADMIN
										</Badge>
									)}
									{member.isModerator && (
										<Badge variant="outline" className="text-xs">
											MOD
										</Badge>
									)}
								</div>
							</div>
						</div>

						<div className="p-6 pt-0 space-y-4">
							{/* Email */}
							<div className="flex items-center gap-3">
								<Icon
									name="Mail"
									className="size-4 text-muted-foreground shrink-0"
								/>
								<span className="text-sm truncate">{user.email}</span>
							</div>

							{/* Joined At */}
							<div className="flex items-center gap-3">
								<Icon
									name="Calendar"
									className="size-4 text-muted-foreground shrink-0"
								/>
								<span className="text-sm">
									Membro desde {format(new Date(member.joinedAt), "dd/MM/yyyy")}
								</span>
							</div>

							{/* Points */}
							<div className="flex items-center gap-3">
								<Icon
									name="Trophy"
									className="size-4 text-muted-foreground shrink-0"
								/>
								<span className="text-sm">{member.points} pontos</span>
							</div>

							{/* Bio */}
							{user.bio && (
								<div className="py-3 border-t">
									<p className="text-sm text-muted-foreground">{user.bio}</p>
								</div>
							)}

							{/* Tags */}
							{member.tags.length > 0 && (
								<div className="pt-3 border-t flex flex-col gap-3">
									<div className="flex items-center gap-2 mb-2">
										<Icon name="Tag" className="size-4 text-muted-foreground" />
										<span className="text-sm font-medium">Tags</span>
									</div>
									<div className="flex flex-wrap gap-1.5">
										{member.tags.map((tag) => (
											<Badge key={tag.id} variant="outline" className="text-xs">
												{tag.text}
											</Badge>
										))}
									</div>
								</div>
							)}

							{/* Badges */}
							{member.badges.length > 0 && (
								<div className="pt-3 border-t flex flex-col gap-3">
									<div className="flex items-center gap-2 mb-2">
										<Icon
											name="Award"
											className="size-4 text-muted-foreground"
										/>
										<span className="text-sm font-medium">Emblemas</span>
									</div>
									<div className="flex flex-wrap gap-1.5">
										{member.badges.map((badge) => (
											<MemberBadge key={badge.id} badge={badge} />
										))}
									</div>
								</div>
							)}
						</div>
					</div>
				</div>

				{/* Right Column - Tabs */}
				<div className="lg:col-span-2">
					<Tabs value={view} onValueChange={onViewChange} className="gap-6">
						<TabsList>
							<TabsTrigger value="acessos" className="border-none">
								Acessos
							</TabsTrigger>
							<TabsTrigger value="atividades" className="border-none">
								Atividades
							</TabsTrigger>
						</TabsList>

						<TabsContent value="acessos">
							<div className="rounded-md border p-6">
								<div className="mb-6 flex items-center justify-between">
									<h3 className="font-semibold">Ofertas</h3>
								</div>

								<Tabs
									value={offersTab}
									onValueChange={(val) =>
										setOffersTab(val as "ativas" | "canceladas")
									}
									className="mb-6"
								>
									<div className="flex flex-row items-center justify-between">
										<TabsList>
											<TabsTrigger value="ativas" className="border-none">
												Ativas ({activeOffers.length})
											</TabsTrigger>
											<TabsTrigger value="canceladas" className="border-none">
												Canceladas ({cancelledOffers.length})
											</TabsTrigger>
										</TabsList>

										<Button
											variant="outline"
											size="sm"
											onClick={() => setIsOfferDialogOpen(true)}
										>
											<Icon name="Plus" className="size-4" />
											Associar Oferta
										</Button>
									</div>
								</Tabs>

								{filteredOffers.length === 0 ? (
									<div className="text-center">
										<Icon
											name="PackageX"
											className="size-10 text-muted-foreground mx-auto mb-3"
										/>
										<p className="text-sm text-muted-foreground">
											{offersTab === "ativas"
												? "Nenhuma oferta ativa."
												: "Nenhuma oferta cancelada."}
										</p>
									</div>
								) : (
									<div className="flex flex-col gap-3">
										{filteredOffers.map((accessGroupEntry) => {
											const accessGroup = accessGroupEntry.accessGroup;
											const createdAt = accessGroupEntry.createdAt;
											const expiresAt = accessGroupEntry.expiresAt;
											const isPaused = !!accessGroupEntry.pausedAt;
											const isExpired =
												expiresAt && new Date(expiresAt) < new Date();

											return (
												<div
													key={accessGroupEntry.id}
													className="flex flex-col gap-3"
												>
													{accessGroup.externalProducts.map(
														(externalProduct) => {
															const product =
																externalProduct.paymentIntegrationProduct;
															if (!product) return null;
															return (
																<div
																	key={externalProduct.id}
																	className="bg-muted border rounded-md p-4 flex items-center gap-4"
																>
																	<div className="flex-1 min-w-0">
																		<div className="flex items-center gap-2 mb-1">
																			<p className="font-medium text-sm truncate">
																				{product.productName}
																			</p>
																			{isPaused && (
																				<Badge
																					variant="secondary"
																					className="text-xs"
																				>
																					Pausada
																				</Badge>
																			)}
																		</div>
																		<div className="flex items-center gap-4 text-xs text-muted-foreground">
																			<span>
																				Início:{" "}
																				{format(
																					new Date(createdAt),
																					"dd/MM/yyyy",
																				)}
																			</span>
																			{expiresAt && (
																				<span>
																					{isExpired ? "Expirou" : "Expira"}:{" "}
																					{format(
																						new Date(expiresAt),
																						"dd/MM/yyyy",
																					)}
																				</span>
																			)}
																			{isPaused &&
																				accessGroupEntry.pausedAt && (
																					<span>
																						Pausada em:{" "}
																						{format(
																							new Date(
																								accessGroupEntry.pausedAt,
																							),
																							"dd/MM/yyyy",
																						)}
																					</span>
																				)}
																		</div>
																	</div>
																	{accessGroupEntry.isActive && (
																		<DropdownMenu>
																			<DropdownMenuTrigger asChild>
																				<Button variant="ghost" size="icon">
																					<Icon name="EllipsisVertical" />
																				</Button>
																			</DropdownMenuTrigger>
																			<DropdownMenuContent align="end">
																				{isPaused ? (
																					<DropdownMenuItem
																						onClick={() => {
																							setExtendExpiration(false);
																							setResumeDialogId(
																								accessGroupEntry.id,
																							);
																						}}
																					>
																						<Icon name="Play" />
																						Retomar
																					</DropdownMenuItem>
																				) : (
																					<DropdownMenuItem
																						onClick={() =>
																							setPauseDialogId(
																								accessGroupEntry.id,
																							)
																						}
																					>
																						<Icon name="Pause" />
																						Pausar
																					</DropdownMenuItem>
																				)}
																				<DropdownMenuItem
																					onClick={() => {
																						setEditDeadlineValue(
																							expiresAt
																								? new Date(expiresAt)
																										.toISOString()
																										.split("T")[0]
																								: "",
																						);
																						setEditDeadlineDialogId(
																							accessGroupEntry.id,
																						);
																					}}
																				>
																					<Icon name="CalendarClock" />
																					Editar prazo
																				</DropdownMenuItem>
																				<DropdownMenuItem
																					onClick={() =>
																						setCancelDialogId(
																							accessGroupEntry.id,
																						)
																					}
																					className="text-destructive"
																				>
																					<Icon name="Ban" />
																					Cancelar oferta
																				</DropdownMenuItem>
																			</DropdownMenuContent>
																		</DropdownMenu>
																	)}
																</div>
															);
														},
													)}
												</div>
											);
										})}
									</div>
								)}
							</div>

							{/* Courses Table */}
							<div className="rounded-md border mt-6">
								<div className="p-4 border-b">
									<h3 className="font-semibold">Cursos</h3>
								</div>
								{courses.length === 0 ? (
									<div className="p-6 text-center">
										<Icon
											name="BookOpen"
											className="size-10 text-muted-foreground mx-auto mb-3"
										/>
										<p className="text-sm text-muted-foreground">
											Nenhum curso encontrado.
										</p>
									</div>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Seção</TableHead>
												<TableHead>Nome</TableHead>
												<TableHead>Turma</TableHead>
												<TableHead>Data de Liberação</TableHead>
												<TableHead>Prazo de Acesso</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{courses.map((courseEntry) => {
												const course = courseEntry.course;
												return (
													<TableRow key={courseEntry.id}>
														<TableCell className="text-muted-foreground">
															{course.category.name}
														</TableCell>
														<TableCell className="font-medium">
															{course.name}
														</TableCell>
														<TableCell>
															{courseEntry.memberGroup.group.name}
														</TableCell>
														<TableCell>
															{format(
																new Date(courseEntry.enrolledAt),
																"dd/MM/yyyy",
															)}
														</TableCell>
														<TableCell>
															{courseEntry.expiresAt ? (
																<span
																	className={
																		courseEntry.isActive
																			? ""
																			: "text-destructive"
																	}
																>
																	{format(
																		new Date(courseEntry.expiresAt),
																		"dd/MM/yyyy",
																	)}
																</span>
															) : (
																<span className="text-muted-foreground">
																	Sem prazo
																</span>
															)}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								)}
							</div>

							{/* Feeds Table */}
							<div className="rounded-md border mt-6">
								<div className="p-4 border-b">
									<h3 className="font-semibold">Feeds</h3>
								</div>
								{spaces.length === 0 ? (
									<div className="p-6 text-center">
										<Icon
											name="MessageSquare"
											className="size-10 text-muted-foreground mx-auto mb-3"
										/>
										<p className="text-sm text-muted-foreground">
											Nenhum feed encontrado.
										</p>
									</div>
								) : (
									<Table>
										<TableHeader>
											<TableRow>
												<TableHead>Grupo</TableHead>
												<TableHead>Feed</TableHead>
												<TableHead>Tipo</TableHead>
												<TableHead>Data de Liberação</TableHead>
												<TableHead>Prazo de Acesso</TableHead>
											</TableRow>
										</TableHeader>
										<TableBody>
											{spaces.map((spaceEntry) => {
												const space = spaceEntry.space;
												return (
													<TableRow key={space.id}>
														<TableCell className="text-muted-foreground">
															{space.group.name}
														</TableCell>
														<TableCell className="font-medium">
															{space.name}
														</TableCell>
														<TableCell>
															<Badge
																variant={
																	space.visibility === "PUBLIC"
																		? "default"
																		: space.visibility === "PRIVATE"
																			? "outline"
																			: "secondary"
																}
																className="gap-1"
															>
																{space.visibility === "PRIVATE" && (
																	<Icon name="Lock" className="size-3" />
																)}
																{space.visibility === "SECRET" && (
																	<Icon name="EyeOff" className="size-3" />
																)}
																{space.visibility === "PUBLIC"
																	? "Público"
																	: space.visibility === "PRIVATE"
																		? "Privado"
																		: "Secreto"}
															</Badge>
														</TableCell>
														<TableCell>
															{format(
																new Date(spaceEntry.joinedAt),
																"dd/MM/yyyy",
															)}
														</TableCell>
														<TableCell>
															{spaceEntry.expiresAt ? (
																<span
																	className={
																		spaceEntry.isActive
																			? ""
																			: "text-destructive"
																	}
																>
																	{format(
																		new Date(spaceEntry.expiresAt),
																		"dd/MM/yyyy",
																	)}
																</span>
															) : (
																<span className="text-muted-foreground">
																	Sem prazo
																</span>
															)}
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								)}
							</div>
						</TabsContent>

						<TabsContent value="atividades">
							<div className="rounded-md border">
								<div className="p-4 border-b">
									<h3 className="font-semibold">Histórico de Atividades</h3>
								</div>
								{historyData.history.edges.length === 0 ? (
									<div className="p-6 text-center">
										<Icon
											name="History"
											className="size-10 text-muted-foreground mx-auto mb-3"
										/>
										<p className="text-sm text-muted-foreground">
											Nenhuma atividade registrada.
										</p>
									</div>
								) : (
									<div className="p-4">
										<div className="relative">
											{/* Timeline line */}
											<div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />

											<div className="space-y-4">
												{historyData.history.edges.map((edge) => {
													const event = edge.node;
													const icon = getHistoryEventIcon(event);

													return (
														<div
															key={event.id}
															className="relative flex gap-4 pl-10"
														>
															{/* Timeline dot */}
															<div className="absolute left-0 w-8 h-8 rounded-full bg-background border-2 border-border flex items-center justify-center">
																<Icon
																	name={icon}
																	className="size-4 text-muted-foreground"
																/>
															</div>

															<div className="flex-1 min-w-0 pt-1">
																<p className="text-sm">
																	<HistoryEventDescription event={event} />
																</p>
																<p className="text-xs text-muted-foreground mt-0.5">
																	{format(
																		new Date(event.createdAt),
																		`dd/MM/yyyy "às" HH:mm`,
																	)}
																</p>
															</div>
														</div>
													);
												})}
											</div>
										</div>

										{hasNext && (
											<div className="mt-4 text-center">
												<Button
													variant="outline"
													size="sm"
													onClick={() => loadNext(10)}
													disabled={isLoadingNext}
												>
													{isLoadingNext ? "Carregando..." : "Ver mais"}
												</Button>
											</div>
										)}
									</div>
								)}
							</div>
						</TabsContent>
					</Tabs>
				</div>
			</div>

			<OfferAssociateDialog
				clubSlug={club?.slug ?? ""}
				username={user.username ?? ""}
				memberName={user.displayName ?? user.username ?? ""}
				open={isOfferDialogOpen}
				onClose={() => setIsOfferDialogOpen(false)}
				onSuccess={onRefresh}
			/>

			{/* Pause Confirmation Dialog */}
			<AlertDialog
				open={!!pauseDialogId}
				onOpenChange={(open) => !open && setPauseDialogId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Pausar assinatura</AlertDialogTitle>
						<AlertDialogDescription>
							Tem certeza que deseja pausar esta assinatura? O membro perderá o
							acesso enquanto a assinatura estiver pausada.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancelar</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => pauseDialogId && handlePause(pauseDialogId)}
						>
							Pausar
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Resume Dialog */}
			<Dialog
				open={!!resumeDialogId}
				onOpenChange={(open) => {
					if (!open) {
						setResumeDialogId(null);
						setExtendExpiration(false);
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Retomar assinatura</DialogTitle>
						<DialogDescription>
							Escolha como deseja retomar esta assinatura.
						</DialogDescription>
					</DialogHeader>

					<RadioGroup
						value={extendExpiration ? "extend" : "keep"}
						onValueChange={(val) => setExtendExpiration(val === "extend")}
						className="gap-4"
					>
						<div className="flex items-start gap-3">
							{/* biome-ignore lint/correctness/useUniqueElementIds: we'll correct it later */}
							<RadioGroupItem value="keep" id="keep" className="mt-0.5" />
							<Label htmlFor="keep" className="font-normal cursor-pointer">
								Manter data de expiração original
							</Label>
						</div>
						<div className="flex items-start gap-3">
							{/* biome-ignore lint/correctness/useUniqueElementIds: we'll correct it later */}
							<RadioGroupItem value="extend" id="extend" className="mt-0.5" />
							<Label htmlFor="extend" className="font-normal cursor-pointer">
								Estender expiração pela duração pausada
								{daysPaused > 0 && (
									<span className="text-muted-foreground">
										{" "}
										({daysPaused} {daysPaused === 1 ? "dia" : "dias"})
									</span>
								)}
							</Label>
						</div>
					</RadioGroup>

					<DialogFooter>
						<Button
							variant="ghost"
							onClick={() => {
								setResumeDialogId(null);
								setExtendExpiration(false);
							}}
						>
							Cancelar
						</Button>
						<Button
							onClick={() => resumeDialogId && handleResume(resumeDialogId)}
						>
							Retomar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Deadline Dialog */}
			<Dialog
				open={!!editDeadlineDialogId}
				onOpenChange={(open) => {
					if (!open) {
						setEditDeadlineDialogId(null);
						setEditDeadlineValue("");
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Editar prazo de acesso</DialogTitle>
						<DialogDescription>
							Defina uma nova data de expiração para esta oferta.
						</DialogDescription>
					</DialogHeader>

					<div className="py-4">
						<Label htmlFor="deadline">Novo prazo</Label>
						{/* biome-ignore lint/correctness/useUniqueElementIds: error */}
						<Input
							id="deadline"
							type="date"
							value={editDeadlineValue}
							onChange={(e) => setEditDeadlineValue(e.target.value)}
							min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
							className="mt-2"
						/>
					</div>

					<DialogFooter>
						<Button
							variant="ghost"
							onClick={() => {
								setEditDeadlineDialogId(null);
								setEditDeadlineValue("");
							}}
						>
							Cancelar
						</Button>
						<Button
							onClick={() =>
								editDeadlineDialogId &&
								editDeadlineValue &&
								handleUpdateExpiration(editDeadlineDialogId, editDeadlineValue)
							}
							disabled={!editDeadlineValue}
						>
							Salvar
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Cancel Offer Confirmation Dialog */}
			<AlertDialog
				open={!!cancelDialogId}
				onOpenChange={(open) => !open && setCancelDialogId(null)}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cancelar oferta</AlertDialogTitle>
						<AlertDialogDescription>
							Tem certeza que deseja cancelar esta oferta? O membro perderá o
							acesso imediatamente.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Voltar</AlertDialogCancel>
						<AlertDialogAction
							onClick={() =>
								cancelDialogId && handleCancelOffer(cancelDialogId)
							}
						>
							Cancelar oferta
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
